import { useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Dialog,
  DialogContent,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridCellParams,
  GridRowId,
  GridRowSelectionModel,
  gridClasses,
} from '@mui/x-data-grid-pro';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as lookupSlice from '../../../data/lookup.slice';
import * as clientSlice from '../../../data/client.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import ClientDialogHeader from './ClientWizardHeader';
import ClientOnboardingWizardFooter from './ClientOnboardingWizardFooter';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { ClientPurposeCode } from '../../../data/models/purpose-code-model.model';
import { ClientTransportationBenefit } from '../../../data/models/transportation-benefit-model.model';
import { ClientTransporationService } from '../../../data/models/transportation-service.model';
import ConfirmationDialog from '../../ConfirmationDialog';

const PREFIX = 'SelectModelsWizard';

const classes = {
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  contentPaper: `${PREFIX}-contentPaper`,
  stepContainer: `${PREFIX}-stepContainer`,
  previousButton: `${PREFIX}-previousButton`,
  grid: `${PREFIX}-grid`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    paddingLeft: theme.spacing(18),
    paddingRight: theme.spacing(18),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
  },

  [`& .${classes.contentPaper}`]: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    borderRadius: 3,
    boxShadow: theme.shadows[2],
    padding: theme.spacing(4),
    height: 'fit-content',
  },

  [`& .${classes.stepContainer}`]: {
    width: '100%',
  },

  [`& .${classes.previousButton}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontFamily: 'Source Sans Pro',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '157%',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },

  [`& .${classes.grid}`]: {
    marginTop: theme.spacing(2),
    width: '580px',
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
    },
    '&.MuiDataGrid-root': {
      border: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitleContainerContent .MuiTypography-root':
      {
        color: theme.palette.lightTextPrimary.main,
        fontFamily: 'Source Sans Pro',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 24,
        letterSpacing: 0.17,
      },

    [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
      {
        outline: 'none',
      },
    '& .MuiSelect-outlined': {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: 'inherit',
    },
  },
}));

enum WizardStep {
  PurposeCode = 0,
  Benefit = 1,
  Service = 2,
}

interface RowItem {
  uuid?: string;
  id: string;
  name: string;
  selected: boolean;
}

interface PurposeCodeRowItem extends RowItem {
  clientName: string;
  type: string;
}

interface SelectModelsWizardProps {
  open: boolean;
  close: () => void;
}

export default function SelectModelsWizard({
  open,
  close,
}: SelectModelsWizardProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const dialogContentRef = useRef(null);

  const client = useAppSelector(clientSlice.selectCurrentClient);
  const purposeCodeModelTypes = useAppSelector(
    lookupSlice.selectPurposeCodeModelTypes,
  );
  const purposeCodeCategories = useAppSelector(
    lookupSlice.selectPurposeCodeCategories,
  );
  const transportationBenefitModels = useAppSelector(
    clientSlice.selectAllTransportationBenefitModels,
  );
  const transportationServices = useAppSelector(
    accountingSlice.selectTransportationServices,
  );
  const clientPurposeCodes = useAppSelector(clientSlice.selectPurposeCodes);
  const isSaving = useAppSelector(clientSlice.selectSavingClientModels);
  const isSaved = useAppSelector(clientSlice.selectSavedClientModels);

  const [title, setTitle] = useState('');
  const [currentColumns, setCurrentColumns] = useState<any>();
  const [purposeCodeRows, setPurposeCodeRows] = useState<PurposeCodeRowItem[]>(
    [],
  );
  const [selectedPurposeCodes, setSelectedPurposeCodes] = useState<GridRowId[]>(
    [],
  );
  const [benefitRows, setBenefitRows] = useState<RowItem[]>([]);
  const [selectedBenefits, setSelectedBenefits] = useState<GridRowId[]>([]);
  const [serviceRows, setServiceRows] = useState<RowItem[]>([]);
  const [selectedServices, setSelectedServices] = useState<GridRowId[]>([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const steps = [
    t('client.purposeCodes'),
    t('client.transportationBenefits'),
    t('client.serviceLevels'),
  ];
  const [activeStep, setActiveStep] = useState(WizardStep.PurposeCode);

  const handleNameChange = (id: string, evt: any) => {
    evt.stopPropagation();
    const idx = purposeCodeRows!.findIndex((r) => r.id === id);
    if (idx >= 0) {
      const newRows = [...purposeCodeRows!];
      (newRows[idx] as PurposeCodeRowItem).clientName = evt.target.value;
      setPurposeCodeRows(newRows);
    }
  };

  const renderClientName = (params: GridCellParams) => {
    return params.row.selected ? (
      <TextField
        value={params.row.clientName}
        onClick={(evt) => evt.stopPropagation()}
        onChange={(evt: any) => handleNameChange(params.row.id, evt)}
        onKeyDown={(evt) => evt.stopPropagation()}
      />
    ) : (
      <></>
    );
  };

  const handleSelectionChange = (id: string, evt: SelectChangeEvent) => {
    evt.stopPropagation();
    const idx = purposeCodeRows!.findIndex((r) => r.id === id);
    if (idx >= 0) {
      const newRows = [...purposeCodeRows!];
      (newRows[idx] as PurposeCodeRowItem).type = evt.target.value;
      setPurposeCodeRows(newRows);
    }
  };

  const renderPurposeCodeType = (params: GridCellParams) => {
    return params.row.selected ? (
      <Select
        value={params.row.type}
        onChange={(evt: SelectChangeEvent) =>
          handleSelectionChange(params.row.id, evt)
        }
        sx={{ width: '100%' }}>
        {purposeCodeModelTypes.map((pcmt) => {
          return (
            <MenuItem
              key={pcmt.purposeCodeModelTypeBC}
              value={pcmt.purposeCodeModelTypeBC}>
              {pcmt.purposeCodeModelTypeName}
            </MenuItem>
          );
        })}
      </Select>
    ) : (
      <></>
    );
  };

  const purposeCodeColumns = [
    {
      field: 'name',
      headerName: t('client.name'),
      flex: 1,
      sortable: false,
    },
    {
      field: 'clientName',
      headerName: t('client.clientPurposeCodeName'),
      flex: 1,
      sortable: false,
      renderCell: renderClientName,
    },
    {
      field: 'type',
      headerName: t('client.purposeCodeType'),
      flex: 1,
      sortable: false,
      renderCell: renderPurposeCodeType,
    },
  ];

  const otherColumns = [
    {
      field: 'name',
      headerName: t('client.name'),
      flex: 1,
      sortable: false,
    },
  ];

  useEffect(() => {
    dispatch(clientSlice.setSavedClientModels(false));
  }, []);

  useEffect(() => {
    if (isSaved) {
      handleClose();
    }
  }, [isSaved]);

  useEffect(() => {
    if (open) {
      const selectedIDs: GridRowId[] = [];
      const rows = purposeCodeCategories.map((p) => {
        const purposeCode = clientPurposeCodes.find(
          (pc) => pc.purposeCodeCategoryBC === p.purposeCodeCategoryBC,
        );
        const selected = !!purposeCode;
        if (selected) {
          selectedIDs.push(p.purposeCodeCategoryBC);
        }
        return {
          uuid: purposeCode?.purposeCodeUUID,
          id: p.purposeCodeCategoryBC,
          name: p.purposeCodeCategoryName,
          clientName: purposeCode?.name || p.purposeCodeCategoryName,
          type:
            purposeCode?.purposeCodeTypeBC ||
            purposeCodeModelTypes[0].purposeCodeModelTypeBC,
          selected: !!purposeCode,
        };
      });
      setPurposeCodeRows(rows);
      setSelectedPurposeCodes(selectedIDs);
    }
  }, [purposeCodeCategories, clientPurposeCodes, open]);

  useEffect(() => {
    if (open) {
      const rows = transportationBenefitModels.map((t) => ({
        id: t.transportationBenefitModelUUID!,
        name: t.name,
        selected: false,
      }));
      setBenefitRows(rows);
    }
  }, [transportationBenefitModels, open]);

  useEffect(() => {
    if (open) {
      const rows = transportationServices.map((t) => ({
        id: t.transportationServiceUUID!,
        name: t.name,
        selected: false,
      }));
      setServiceRows(rows);
    }
  }, [transportationServices, open]);

  useEffect(() => {
    setTitle(steps[activeStep]);
    setCurrentColumns(activeStep === 0 ? purposeCodeColumns : otherColumns);
  }, [activeStep]);

  const getPurposeCodesToSave = () => {
    const purposeCodes: ClientPurposeCode[] = [];
    (purposeCodeRows as PurposeCodeRowItem[]).forEach((pcr) => {
      if (pcr.selected) {
        purposeCodes.push({
          purposeCodeUUID: pcr.uuid,
          clientBC: client!.clientBC,
          purposeCodeCategoryBC: pcr.id,
          name: pcr.clientName || pcr.name,
          purposeCodeTypeBC: pcr.type,
        });
      } else if (pcr.uuid) {
        // These will get deleted because they were selected when we restarted the wizard
        // but then got unselected.
        purposeCodes.push({
          purposeCodeUUID: pcr.uuid,
          clientBC: client!.clientBC,
          purposeCodeCategoryBC: '',
          name: '',
          purposeCodeTypeBC: '',
        });
      }
    });
    return purposeCodes;
  };

  const resetForm = () => {
    setSelectedPurposeCodes([]);
    setSelectedBenefits([]);
    setSelectedServices([]);
    purposeCodeRows.forEach((row) => (row.selected = false));
  };

  const saveData = () => {
    const selectedBenefitRows: RowItem[] = [];
    const selectedServiceRows: RowItem[] = [];
    const purposeCodes = getPurposeCodesToSave();
    const benefits: ClientTransportationBenefit[] = selectedBenefitRows.map(
      (b) => {
        return {
          clientBC: client!.clientBC,
          transportationBenefitUUID: b.id,
        };
      },
    );
    const services: ClientTransporationService[] = selectedServiceRows.map(
      (s) => {
        return {
          clientBC: client!.clientBC,
          transportationServiceUUID: s.id,
        };
      },
    );
    dispatch(
      clientSlice.saveClientModelsAsync(
        client!.clientBC,
        purposeCodes,
        benefits,
        services,
      ),
    );
  };

  const scrollToTop = () => {
    if (dialogContentRef.current) {
      (dialogContentRef.current as any).scrollTop = 0;
    }
  };

  const updateStep = (newStep: number) => {
    if (newStep > 2) {
      saveData();
    } else {
      setActiveStep(newStep);
      scrollToTop();
    }
  };

  const handlePrevious = () => {
    updateStep(activeStep - 1);
  };

  const handleStep = (step: number) => () => {
    updateStep(step);
  };

  const handleNext = () => {
    updateStep(activeStep + 1);
  };

  const handleClose = () => {
    setConfirmDialogOpen(false);
    setActiveStep(0);
    resetForm();
    close();
  };

  const formHasChanges = (): boolean => {
    const selected = getPurposeCodesToSave();
    const hasPurposeCodeChanges = selected.reduce((prev, cur) => {
      const oldSelected = clientPurposeCodes.find(
        (pc) => pc.purposeCodeCategoryBC === cur.purposeCodeCategoryBC,
      );
      if (oldSelected) {
        return (
          prev ||
          oldSelected.purposeCodeTypeBC !== cur.purposeCodeTypeBC ||
          oldSelected.name !== cur.name
        );
      } else {
        return true;
      }
    }, false);

    // TODO: this will have to be updated to look for changes in benefits and services
    // once we are able to save and load those.
    const hasBenefitChanges = selectedBenefits.length > 0;
    const hasServiceChanges = selectedServices.length > 0;

    return hasPurposeCodeChanges || hasBenefitChanges || hasServiceChanges;
  };

  const cancel = () => {
    if (formHasChanges()) {
      setConfirmDialogOpen(true);
    } else {
      handleClose();
    }
  };

  const handleSaveAndClose = () => {
    saveData();
  };

  const handleRowSelectionModelChange = (newModel: GridRowSelectionModel) => {
    switch (activeStep) {
      case WizardStep.PurposeCode:
        setSelectedPurposeCodes([...newModel]);
        setPurposeCodeRows(
          purposeCodeRows.map((r) => ({
            ...r,
            selected: newModel.includes(r.id),
          })),
        );
        break;
      case WizardStep.Benefit:
        setSelectedBenefits([...newModel]);
        setBenefitRows(
          benefitRows.map((r) => ({ ...r, selected: newModel.includes(r.id) })),
        );
        break;
      case WizardStep.Service:
        setSelectedServices([...newModel]);
        setServiceRows(
          serviceRows.map((r) => ({ ...r, selected: newModel.includes(r.id) })),
        );
        break;
    }
  };

  const currentRows = useMemo((): RowItem[] => {
    let rows: RowItem[] = [];
    switch (activeStep) {
      case WizardStep.PurposeCode:
        rows = purposeCodeRows;
        setCurrentColumns(purposeCodeColumns);
        break;
      case WizardStep.Benefit:
        rows = benefitRows;
        setCurrentColumns(otherColumns);
        break;
      case WizardStep.Service:
        rows = serviceRows;
        setCurrentColumns(otherColumns);
        break;
    }
    return rows;
  }, [activeStep, purposeCodeRows, benefitRows, serviceRows]);

  const currentSelectionModel = useMemo((): GridRowId[] => {
    switch (activeStep) {
      case WizardStep.PurposeCode:
        return selectedPurposeCodes;
      case WizardStep.Benefit:
        return selectedBenefits;
      case WizardStep.Service:
        return selectedServices;
    }
  }, [activeStep, selectedPurposeCodes, selectedBenefits, selectedServices]);

  const isSelectionValid = (): boolean => {
    let isValid = false;
    switch (activeStep) {
      case WizardStep.PurposeCode:
        isValid = !!selectedPurposeCodes.length;
        break;
      case WizardStep.Benefit:
        isValid = !!selectedBenefits.length;
        break;
      case WizardStep.Service:
        isValid = !!selectedServices.length;
        break;
    }
    return isValid;
  };

  return (
    <RootDialog fullScreen open={open} onClose={handleClose}>
      <ClientDialogHeader
        passengerName={`${client?.legalName || ''}`}
        title={t('client.selectModels')}
        steps={steps}
        activeStep={activeStep}
        isValid={true}
        step={handleStep}
        close={cancel}
      />
      <DialogContent className={classes.content} ref={dialogContentRef}>
        <Paper className={classes.contentPaper}>
          {activeStep > 0 && (
            <Box className={classes.previousButton} onClick={handlePrevious}>
              <ArrowBackIos sx={{ fontSize: 14 }} />
              {t('common.back')}
            </Box>
          )}
          <Typography variant="h5" className={classes.title}>
            {title}
          </Typography>
          <DataGridPro
            getRowId={(row) => row.id}
            columns={currentColumns}
            rows={currentRows}
            className={classes.grid}
            checkboxSelection
            hideFooter={true}
            disableColumnMenu={true}
            rowSelectionModel={currentSelectionModel}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            loading={!currentRows.length}
            autoHeight={true}
            disableColumnResize={true}
            disableColumnReorder={true}
          />
          <ClientOnboardingWizardFooter
            isLast={activeStep === steps.length - 1}
            isSaving={isSaving}
            isValid={isSelectionValid()}
            next={handleNext}
            saveAndClose={handleSaveAndClose}
          />
        </Paper>
      </DialogContent>
      <ConfirmationDialog
        title={t('client.confirmCancelTitle')}
        message={t('client.confirmCancelMessage')}
        open={confirmDialogOpen}
        positiveAction={handleClose}
        negativeAction={() => setConfirmDialogOpen(false)}
      />
    </RootDialog>
  );
}
